import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

interface Props {
  to: string;
  refresh?: boolean;
  /**
   * Replace the current history entry instead of adding one. For leaving a page that must not be
   * returned to with Back, such as `/login` once the visitor is signed in.
   */
  replace?: boolean;
}

/**
 * Navigate once per destination, not once per render.
 *
 * `useRouter()` does not hand back a stable object: Next builds a fresh one on every render
 * (`makePublicRouterInstance`, `next/dist/client/index.js:222`, which copies `query` so that it
 * "is not stateful"). So `router` in the dependency array made this effect re-run on **every**
 * render of this component, and re-issue the same `router.push`. Each one cancels the
 * navigation started by the one before it, and Next answers the interrupted one with a
 * `routeChangeError` carrying `cancelled: true` (`shared/lib/router/router.js:864`).
 *
 * This component sits directly under `AuthGuard`, which re-renders on every change to the auth
 * context, so the repeats are not hypothetical — but they are churn rather than breakage, since
 * each cancellation is still reported and every listener bound to the router's events still
 * sees a balanced pair. It is worth fixing because a redundant navigation is indistinguishable
 * from a real one at every one of those listeners, not because it was ever observed to break
 * something. (It was briefly suspected of causing a stuck progress bar on
 * `/candidature/:id/intervista`; it was not the cause — see `progress-bar.tsx`.)
 *
 * The ref is on the destination and not a boolean, so a genuinely different `to` is still
 * honoured — the same reasoning as `shouldVerifyToken` for the magic-link token. `refresh` is
 * guarded by it too: a full-page `location.replace` fired once per render is the same defect
 * with a heavier hammer.
 */
export function Redirect({ to, refresh = false, replace = false }: Props) {
  const router = useRouter();
  const isReady = router.isReady;
  const navigatedTo = useRef<string | null>(null);

  useEffect(() => {
    if (!to || navigatedTo.current === to) return;

    if (refresh) {
      navigatedTo.current = to;
      window?.location?.replace(to);
      return;
    }

    if (!isReady) return;

    navigatedTo.current = to;
    if (replace) router.replace(to);
    else router.push(to);
    // `router` is deliberately not a dependency: it changes identity on every render, which is
    // the whole defect above. The value used here is read at call time and the ref guards the
    // repeat, so nothing is stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, refresh, replace, isReady]);

  return null;
}
